import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { User, Save, Eye, EyeOff, Image, Link as LinkIcon, School, Award, Palette, FileText } from "lucide-react";
import { Button } from "../ui/button";
import axios from "axios";
import { API, getAuthHeader } from "./utils";

export const FeaturedAthleteTab = () => {
  const [athlete, setAthlete] = useState({
    name: "",
    school: "",
    sport: "",
    image_url: "",
    background_image_url: "",
    description: "",
    school_logo_url: "",
    school_color: "#F97316",
    article_id: "",
    flagship_sponsor: { name: "", logo_url: "", url: "" },
    is_enabled: false
  });
  const [articles, setArticles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [athleteRes, articlesRes] = await Promise.all([
        axios.get(`${API}/featured-athlete/admin`, { headers: getAuthHeader() }),
        axios.get(`${API}/news/articles?limit=50`, { headers: getAuthHeader() })
      ]);
      
      const data = athleteRes.data || {};
      setAthlete({
        name: data.name || "",
        school: data.school || "",
        sport: data.sport || "",
        image_url: data.image_url || "",
        background_image_url: data.background_image_url || "",
        description: data.description || "",
        school_logo_url: data.school_logo_url || "",
        school_color: data.school_color || "#F97316",
        article_id: data.article_id || "",
        flagship_sponsor: data.flagship_sponsor || { name: "", logo_url: "", url: "" },
        is_enabled: data.is_enabled || false
      });
      
      setArticles(articlesRes.data?.articles || []);
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!athlete.name || !athlete.school || !athlete.sport || !athlete.image_url) {
      alert("Name, school, sport, and image are required");
      return;
    }

    setSaving(true);
    try {
      await axios.put(`${API}/featured-athlete/admin`, athlete, {
        headers: getAuthHeader()
      });
      alert("Featured athlete saved successfully!");
    } catch (err) {
      console.error("Error saving:", err);
      alert("Failed to save featured athlete");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase text-white">
            Athlete of the Week
          </h2>
          <p className="text-zinc-500 text-sm mt-1">
            Highlight an athlete on the homepage
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setAthlete({ ...athlete, is_enabled: !athlete.is_enabled })}
            className={`flex items-center gap-2 px-4 py-2 border ${
              athlete.is_enabled
                ? "border-green-500/50 bg-green-500/10 text-green-400"
                : "border-white/10 bg-zinc-800 text-zinc-400"
            }`}
          >
            {athlete.is_enabled ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            {athlete.is_enabled ? "Enabled" : "Disabled"}
          </button>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-orange-500 text-black hover:bg-orange-400"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving..." : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Form */}
        <div className="space-y-6">
          {/* Athlete Info */}
          <div className="bg-zinc-900/50 border border-white/10 p-6 space-y-4">
            <h3 className="text-xs font-bold uppercase text-zinc-400 mb-4">Athlete Info</h3>
            
            {/* Name */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <User className="w-3 h-3 inline mr-1" />
                Athlete Name *
              </label>
              <input
                type="text"
                value={athlete.name}
                onChange={(e) => setAthlete({ ...athlete, name: e.target.value })}
                placeholder="John Smith"
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Sport */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <Award className="w-3 h-3 inline mr-1" />
                Sport *
              </label>
              <input
                type="text"
                value={athlete.sport}
                onChange={(e) => setAthlete({ ...athlete, sport: e.target.value })}
                placeholder="Basketball"
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* Photo URL */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <Image className="w-3 h-3 inline mr-1" />
                Photo URL *
              </label>
              <input
                type="url"
                value={athlete.image_url}
                onChange={(e) => setAthlete({ ...athlete, image_url: e.target.value })}
                placeholder="https://..."
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              />
              <p className="text-xs text-zinc-600 mt-1">Athlete photo (shown in foreground)</p>
            </div>

            {/* Background Image URL */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <Image className="w-3 h-3 inline mr-1" />
                Background Image URL
              </label>
              <input
                type="url"
                value={athlete.background_image_url || ""}
                onChange={(e) => setAthlete({ ...athlete, background_image_url: e.target.value })}
                placeholder="https://..."
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              />
              <p className="text-xs text-zinc-600 mt-1">Background image (shown on the right with color fade)</p>
            </div>

            {/* Description */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <FileText className="w-3 h-3 inline mr-1" />
                Description
              </label>
              <textarea
                value={athlete.description}
                onChange={(e) => setAthlete({ ...athlete, description: e.target.value })}
                placeholder="Write a short bio or achievement description..."
                rows={4}
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500 resize-none"
              />
            </div>

            {/* Article Link */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <LinkIcon className="w-3 h-3 inline mr-1" />
                Link to Article (Optional)
              </label>
              <select
                value={athlete.article_id}
                onChange={(e) => setAthlete({ ...athlete, article_id: e.target.value })}
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              >
                <option value="">No article linked</option>
                {articles.filter(a => a.status === "published").map((article) => (
                  <option key={article.id} value={article.id}>
                    {article.title}
                  </option>
                ))}
              </select>
              <p className="text-zinc-600 text-xs mt-1">
                If linked, a &quot;Read More&quot; link will appear
              </p>
            </div>

            {/* Flagship Sponsor Section */}
            <div className="border-t border-zinc-800 pt-6 mt-6">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
                Flagship Sponsor (Optional)
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-2 uppercase">
                    Sponsor Name
                  </label>
                  <input
                    type="text"
                    value={athlete.flagship_sponsor?.name || ""}
                    onChange={(e) => setAthlete({ 
                      ...athlete, 
                      flagship_sponsor: { ...athlete.flagship_sponsor, name: e.target.value } 
                    })}
                    placeholder="e.g., Nike, Gatorade"
                    className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-2 uppercase">
                    Sponsor Logo URL
                  </label>
                  <input
                    type="url"
                    value={athlete.flagship_sponsor?.logo_url || ""}
                    onChange={(e) => setAthlete({ 
                      ...athlete, 
                      flagship_sponsor: { ...athlete.flagship_sponsor, logo_url: e.target.value } 
                    })}
                    placeholder="https://..."
                    className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-2 uppercase">
                    Sponsor Website URL
                  </label>
                  <input
                    type="url"
                    value={athlete.flagship_sponsor?.url || ""}
                    onChange={(e) => setAthlete({ 
                      ...athlete, 
                      flagship_sponsor: { ...athlete.flagship_sponsor, url: e.target.value } 
                    })}
                    placeholder="https://..."
                    className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* School Info */}
          <div className="bg-zinc-900/50 border border-white/10 p-6 space-y-4">
            <h3 className="text-xs font-bold uppercase text-zinc-400 mb-4">School Info</h3>
            
            {/* School Name */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <School className="w-3 h-3 inline mr-1" />
                School Name *
              </label>
              <input
                type="text"
                value={athlete.school}
                onChange={(e) => setAthlete({ ...athlete, school: e.target.value })}
                placeholder="Tyler Junior College"
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              />
            </div>

            {/* School Logo */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <Image className="w-3 h-3 inline mr-1" />
                School Logo URL
              </label>
              <input
                type="url"
                value={athlete.school_logo_url}
                onChange={(e) => setAthlete({ ...athlete, school_logo_url: e.target.value })}
                placeholder="https://..."
                className="w-full bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
              />
              <p className="text-zinc-600 text-xs mt-1">
                This logo will appear in the circular badge
              </p>
            </div>

            {/* School Color */}
            <div>
              <label className="block text-xs text-zinc-500 mb-2 uppercase">
                <Palette className="w-3 h-3 inline mr-1" />
                School Primary Color
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={athlete.school_color}
                  onChange={(e) => setAthlete({ ...athlete, school_color: e.target.value })}
                  className="w-12 h-12 bg-transparent border border-white/10 cursor-pointer"
                />
                <input
                  type="text"
                  value={athlete.school_color}
                  onChange={(e) => setAthlete({ ...athlete, school_color: e.target.value })}
                  placeholder="#F97316"
                  className="flex-1 bg-black border border-white/10 text-white px-4 py-3 focus:outline-none focus:border-orange-500"
                />
              </div>
              <p className="text-zinc-600 text-xs mt-1">
                Section background will match this color
              </p>
            </div>
          </div>
        </div>

        {/* Preview */}
        <div className="space-y-4">
          <h3 className="text-xs font-bold uppercase text-zinc-400">Preview</h3>
          
          <div 
            className="border border-white/10 overflow-hidden"
            style={{ backgroundColor: athlete.school_color ? `${athlete.school_color}15` : '#0a1628' }}
          >
            <div className="grid md:grid-cols-2 min-h-[400px]">
              {/* Left - Info */}
              <div className="p-6 flex flex-col border-r border-white/10">
                {/* School Logo */}
                <div className="mb-6">
                  {athlete.school_logo_url ? (
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center overflow-hidden"
                      style={{ backgroundColor: athlete.school_color }}
                    >
                      <img src={athlete.school_logo_url} alt="" className="w-12 h-12 object-contain" />
                    </div>
                  ) : (
                    <div 
                      className="w-16 h-16 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: athlete.school_color }}
                    >
                      <span className="text-black font-bold text-lg">
                        {athlete.school?.charAt(0) || "?"}
                      </span>
                    </div>
                  )}
                </div>

                <div className="text-xs text-zinc-400 uppercase tracking-wider mb-4">
                  Athlete of the Week
                </div>

                <h3 
                  className="text-3xl font-heading uppercase tracking-tight mb-2"
                  style={{ color: athlete.school_color }}
                >
                  {athlete.name || "Athlete Name"}
                </h3>
                
                <p className="text-white font-bold uppercase tracking-wider mb-4">
                  {athlete.sport || "Sport"}
                </p>
                
                <p className="text-zinc-400 text-sm leading-relaxed flex-1">
                  {athlete.description || "Add a description to show the athlete's achievements and bio here."}
                </p>

                {athlete.article_id && (
                  <div className="mt-4">
                    <span style={{ color: athlete.school_color }} className="font-bold uppercase text-sm">
                      Read More →
                    </span>
                  </div>
                )}
              </div>

              {/* Right - Photo */}
              <div className="relative aspect-[4/3] md:aspect-auto bg-zinc-800">
                {athlete.image_url ? (
                  <img
                    src={athlete.image_url}
                    alt={athlete.name || "Athlete"}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = "https://placehold.co/400x500/1a1a1a/666?text=Image+Error";
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-zinc-600">
                    <User className="w-16 h-16" />
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {!athlete.is_enabled && (
            <div className="p-4 bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-yellow-400 text-sm">
                This section is currently disabled and won&apos;t appear on the homepage.
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default FeaturedAthleteTab;
